export default class OPProxyClient {
  constructor(log) {
    this.statusCode = 0;
    this.proxyCode = 0;
    this.maxLen = 20;
    this._service = 0x1823;
    this.notified = false;
    this.log = log;
  }

  async connect() {
    if (typeof this.device !== 'undefined') {
      if (this.device.gatt.connected) {
        return;
      }
    }
    this.log('Requesting Bluetooth Device...');
    this.device = await navigator.bluetooth.requestDevice({
      filters: [{ services: [this._service] }],
    });

    this.log('Connecting to GATT Server...');
    const server = await this.device.gatt.connect();

    this.log('Getting HTTP Service...');
    const service = await server.getPrimaryService(this._service);

    this.log('Getting URI Characteristic...');
    this._uri_c = await service.getCharacteristic(0x2ab6);

    this.log('Getting HTTP Entity Body Characteristic...');
    this._http_entity_body_c = await service.getCharacteristic(0x2ab9);

    this.log('Getting HTTP header Characteristic...');
    this._http_headers_c = await service.getCharacteristic(0x2ab7);

    this.log('Getting HTTP Control Point Characteristic...');
    this._http_control_point_c = await service.getCharacteristic(0x2aba);

    await this.reset(); // Clear any stale data

    this.log('Getting http_status_code Characteristic...');
    this._http_status_code_c = await service.getCharacteristic(0x2ab8);

    await this._http_status_code_c.startNotifications();
    this.log('> Notifications started');

    this._http_status_code_c.addEventListener(
      'characteristicvaluechanged',
      (event) => {
        const hex = event.target.value;
        this.statusCode = (hex.getUint8(0) + (hex.getUint8(1) << 8)).toString();
        this.proxyCode = hex.getUint8(2);
        this.log(`> Status value is ${this.statusCode} %`);
        this.log(`> Notification value is ${this.proxyCode} %`);
        this.notified = true;
      },
    );
  }

  async _writeC(characteristic, payload) {
    // We need to chunk over long headers. A length of 20 is pretty
    // conservative, since it is the payload size with the default
    // mtu.
    const enc = new TextEncoder();
    const dec = new TextDecoder('utf-8');

    const bufPayload =
      typeof payload === 'string' ? enc.encode(payload) : payload;
    const strPayload = dec.decode(bufPayload);

    if (bufPayload.length > this.maxLen) {
      this.log(
        `Writing (${characteristic}): ${strPayload.slice(0, this.maxLen)}`,
      );
      await this[characteristic].writeValue(bufPayload.slice(0, this.maxLen));
      return this._writeC(characteristic, bufPayload.slice(this.maxLen));
    }

    this.log(`Writing (${characteristic}): ${strPayload}`);
    return this[characteristic].writeValue(bufPayload);
  }

  async _readC(characteristic) {
    // This reads a BLE characteristic until we get back an empty response.
    // By waiting for an empty response, we chunk results longer than the mtu.
    let resp = '';
    const dec = new TextDecoder('utf-8');

    while (true) {
      this.log('Reading chunk');
      // eslint-disable-next-line no-await-in-loop
      const body = await this[characteristic].readValue();
      resp += dec.decode(body);
      if (body.byteLength === 0) {
        break;
      }
    }

    this.log(`Read (${characteristic}): ${resp}`);
    return resp;
  }

  async reset() {
    const resetCode = Uint8Array.of(0);
    this.log('Performing RESET');
    await this._writeC('_http_control_point_c', resetCode);
  }

  async _do(method, uri, headers, body) {
    this.notified = false;

    await this._writeC('_uri_c', uri);
    await this._writeC('_http_headers_c', headers);
    await this._writeC('_http_entity_body_c', body);
    await this._writeC('_http_control_point_c', method);

    return new Promise((resolve, reject) => {
      let timeout = null;
      const timer = setInterval(async () => {
        if (this.notified === true) {
          clearInterval(timer);
          if (timeout) clearTimeout(timeout);
          const result = { status: this.statusCode };

          // The proxy code part of the response tells us if we have
          // headers or body waiting.  The two values for each are the
          // truncated vs non-truncated versions.  Since we are
          // chunking they are the same for our opproxy.
          if (this.proxyCode & (1 << 0) || this.proxyCode & (1 << 1)) {
            this.log('Fetching Headers');
            result.headers = await this._readC('_http_headers_c');
          }
          if (this.proxyCode & (1 << 2) || this.proxyCode & (1 << 3)) {
            this.log('Fetching Body');
            result.body = await this._readC('_http_entity_body_c');
          }
          resolve(result);
        }
        // else {
        //   console.log('tick')
        // }
      }, 100);

      timeout = setTimeout(() => {
        clearInterval(timer);
        reject(new Error('Failed to get notification'));
      }, 5000);
    });
  }

  post(uri, headers, body) {
    const postCode = Uint8Array.of(3);
    this.log('Performing POST');
    return this._do(postCode, uri, headers, body);
  }

  get(uri, headers, body) {
    const getCode = Uint8Array.of(1);
    this.log('Performing GET');
    return this._do(getCode, uri, headers, body);
  }
}
